import {
  createManualCustomerLead,
  editCustomerLeadContact,
} from "@/data/firestore/leadActivityRepository";
import { EstimateValue } from "@/discovery/edit-mode";
import { EstimatePrice, PartialEstimateNotice } from "@/discovery/estimate-notice";
import { estimateLabelKey } from "@/discovery/estimate-presentation";
import { LeadActivityPanel } from "@/components/LeadActivityPanel";
import { ProjectSummary } from "@/components/ProjectSummary";
import { LeadModeBadge } from "@/components/LeadModeBadge";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { useI18n } from "@/lib/i18n";
import { PageHeader } from "@/components/PageHeader";
import { leadStatuses } from "@/domain/types";
import { pageTitle } from "@/lib/brand";
import { useLeadsStore } from "@/stores/leadsStore";
import { EmptyState, ErrorState, LoadingState } from "@/components/DataState";
import {
  LeadManagement,
  LeadContact,
  LeadSummary,
  LeadStatusBadge,
} from "@/components/LeadManagement";
import { useLeadCounts } from "@/hooks/useLeadCounts";
import { useDashboardAccess } from "@/components/dashboard-access-context";
import { canViewOwnerOperations } from "@/lib/access-control";

export const Route = createFileRoute("/app/$customerId/leads")({
  head: () => ({
    meta: [
      { title: pageTitle("Leads") },
      {
        name: "description",
        content:
          "Every lead arrives with a conversation summary, the customer's answers, the estimate and Activity history.",
      },
      { property: "og:title", content: pageTitle("Leads") },
      {
        property: "og:description",
        content: "Qualified leads with summary, answers, estimate and Activity.",
      },
    ],
  }),
  component: Leads,
});

function Leads() {
  const { customerId } = Route.useParams();
  const access = useDashboardAccess();
  const canViewOperations = canViewOwnerOperations(customerId, access);
  const { t, tl, formatCurrency } = useI18n();
  const { leads, status: loadStatus, error } = useLeadsStore();
  const counts = useLeadCounts(
    customerId,
    canViewOperations,
    leads.map((lead) => `${lead.id}:${lead.status}`).join("|"),
  );
  useEffect(() => {
    if (!canViewOperations) {
      useLeadsStore.getState().stop();
      return;
    }
    useLeadsStore.getState().start(customerId);
    return () => useLeadsStore.getState().stop();
  }, [customerId, canViewOperations]);

  if (!canViewOperations)
    return (
      <div>
        <PageHeader title={t("leads.title")} subtitle={t("leads.subtitle")} />
        <EmptyState message="Lead details are available only to authorized company users." />
      </div>
    );

  return (
    <div>
      <PageHeader title={t("leads.title")} subtitle={t("leads.subtitle")} />

      <LeadSummary
        loading={counts.loading}
        error={counts.error}
        values={counts.values.map(([key, value]) => [
          key === "total" ? t("leads.total") : t(`status.${key}`),
          value,
        ])}
      />
      <p className="mb-3 text-xs text-muted-foreground">
        {leads.length} {t("leads.title")} · {t("leads.recentLimit")}
      </p>
      <LeadManagement
        key={customerId}
        onCreate={
          access.mode === "manage" && access.userId && !access.pending
            ? (values) => createManualCustomerLead(customerId, values)
            : undefined
        }
        loading={loadStatus === "loading" || loadStatus === "idle"}
        error={error}
        statuses={leadStatuses}
        items={leads.map((lead) => ({
          id: lead.id,
          leadOrigin: lead.leadOrigin,
          name: lead.name,
          status: lead.status,
          searchText: [
            lead.name,
            lead.contact.email,
            lead.contact.phone,
            lead.contact.business,
            lead.contact.website,
            tl(lead.solution),
          ].join(" "),
          subtitle: lead.leadOrigin === "manual" ? lead.contact.business || "" : tl(lead.solution),
          badge: lead.leadOrigin !== "manual" ? <LeadModeBadge mode={lead.mode} /> : undefined,
          trailing: (
            <span className="shrink-0 text-end">
              {lead.leadOrigin !== "manual" && (
                <>
                  <span className="num block text-sm font-bold">
                    {lead.estimatePresentation?.partial && (
                      <span className="block text-xs">{t("disc.partialEstimate")}</span>
                    )}
                    {lead.estimatePresentation ? (
                      <EstimateValue
                        estimate={lead.estimatePresentation.estimate}
                        display={lead.estimatePresentation.display}
                      />
                    ) : lead.estimateLow === null ? (
                      t("disc.quoteRequired")
                    ) : (
                      formatCurrency(lead.estimateLow, lead.estimateCurrency)
                    )}
                  </span>
                </>
              )}
              <span className="block text-xs">{formatDate(lead.createdAt)}</span>
            </span>
          ),
        }))}
        renderDetail={(id) => {
          const selected = leads.find((lead) => lead.id === id)!;
          return (
            <>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="flex flex-wrap items-center gap-2 text-xl font-extrabold">
                    {selected.name}{" "}
                    {selected.leadOrigin !== "manual" && <LeadModeBadge mode={selected.mode} />}
                  </h2>
                  {selected.mode === "demo" && (
                    <p className="mt-1 text-xs text-muted-foreground">{t("leads.demoHelp")}</p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    {selected.location} · {formatDate(selected.createdAt)}
                  </p>
                </div>
                <LeadStatusBadge status={selected.status} />
              </div>

              <LeadContact
                contact={selected.contact}
                onEdit={
                  access.mode === "manage" && access.userId && !access.pending
                    ? (field, value) =>
                        editCustomerLeadContact(customerId, selected.id, field, value)
                    : undefined
                }
              />
              {selected.leadOrigin !== "manual" && (
                <>
                  <section
                    className="discovery-panel mt-5 p-4 shadow-none"
                    aria-label={t("disc.summary")}
                  >
                    <ProjectSummary
                      compact
                      showUnderstanding={false}
                      recommendation={tl(selected.solution)}
                      estimate={
                        selected.displayedEstimate ? (
                          <EstimatePrice
                            saved={selected.displayedEstimate}
                            estimate={null}
                            display={selected.displayedEstimate.display}
                            tax={null}
                          />
                        ) : selected.estimatePresentation ? (
                          <EstimateValue
                            estimate={selected.estimatePresentation.estimate}
                            display={selected.estimatePresentation.display}
                          />
                        ) : selected.estimateLow === null || selected.estimateHigh === null ? (
                          t("disc.quoteRequired")
                        ) : (
                          <>
                            {formatCurrency(selected.estimateLow, selected.estimateCurrency)} –{" "}
                            {formatCurrency(selected.estimateHigh, selected.estimateCurrency)}
                          </>
                        )
                      }
                      estimateLabelKey={
                        selected.estimatePresentation
                          ? estimateLabelKey(
                              selected.estimatePresentation.display,
                              "summary",
                              selected.estimatePresentation.partial ? "partial" : undefined,
                            )
                          : "disc.estimate"
                      }
                      estimateNote={
                        <PartialEstimateNotice
                          excluded={selected.estimatePresentation?.excluded ?? []}
                        />
                      }
                      confidence={selected.confidence}
                    />
                  </section>

                  <section className="mt-6">
                    <h3 className="text-sm font-bold">{t("leads.answers")}</h3>
                    {selected.visitorNotes && (
                      <p className="mt-3 whitespace-pre-wrap text-sm">{selected.visitorNotes}</p>
                    )}
                    <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                      {selected.answers.map((a) => (
                        <li
                          key={a.id}
                          className="rounded-2xl border border-border bg-secondary/40 p-3.5"
                        >
                          <p className="text-[11px] text-muted-foreground">{tl(a.q)}</p>
                          <p className="mt-0.5 text-sm font-semibold">{tl(a.a)}</p>
                        </li>
                      ))}
                    </ul>
                  </section>

                  {selected.customerNotes?.trim() && (
                    <section className="mt-6" aria-label={t("leads.customerNotes")}>
                      <h3 className="text-sm font-bold">{t("leads.customerNotes")}</h3>
                      <p className="mt-3 whitespace-pre-wrap break-words text-sm">
                        {selected.customerNotes}
                      </p>
                    </section>
                  )}
                </>
              )}
              <LeadActivityPanel
                key={`${customerId}:${selected.id}`}
                customerId={customerId}
                leadId={selected.id}
                status={selected.status}
              />
            </>
          );
        }}
      />
    </div>
  );
}

function formatDate(date: Date) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
    date,
  );
}
